// Types shared across the Worker API and the React client.

export interface Species {
  taxon_id: number;
  common_name: string;
  scientific_name: string;
  observation_count: number;
  photo_url: string;
  category?: string; // taxonomic group: Fish, Sharks & rays, Molluscs, …
}

export interface GeoLocation {
  name: string;
  lat: number;
  lng: number;
}

export interface SpeciesResponse {
  location: GeoLocation;
  radius_km: number;
  research_only: boolean;
  species: Species[];
  // Diagnostic — bumped whenever the sort order changes. Lets a client
  // distinguish "cache-still-holding-stale-order" from "server returned a
  // wrong order" without cross-referencing the deploy hash.
  sort_version?: string;
}

// ----- /api/identify -----

export interface IdentifyCandidate {
  common: string;
  scientific: string;
  count: number;
}

export interface IdentifyRequest {
  description: string;
  location_name: string;
  candidates: IdentifyCandidate[];
}

export interface IdentifyMatch {
  scientific_name: string;
  common_name: string;
  confidence: number; // 0..1
  reasoning: string;
  caveat: string;
}

export type IdentifyQuality = "ok" | "weak" | "poor";

// Surfaced so we can judge, from real use, whether the current model is good
// enough or we should move to a stronger one (Llama 70B, Anthropic Opus, …).
export interface IdentifyMeta {
  provider: string; // e.g. "workers-ai"
  model: string; // model id actually used
  model_label: string; // friendly name for the UI
  match_count: number;
  top_confidence: number; // best match confidence, 0..1
  json_repaired: boolean; // model didn't return clean JSON
  quality: IdentifyQuality;
}

export interface IdentifyResponse {
  matches: IdentifyMatch[];
  meta: IdentifyMeta;
}

// ----- /api/identify-photo (vision) -----

export interface IdentifyPhotoRequest {
  image: string; // data URL or bare base64 of a (client-downscaled) JPEG
  location_name: string;
  candidates: IdentifyCandidate[];
}

export interface IdentifyPhotoResponse extends IdentifyResponse {
  // Stable URL of the persisted user photo (served from R2), or "" when
  // object storage isn't configured — matches still come back either way.
  photo_url: string;
}

// Video identify (F18) — client extracts N still frames from the video
// via canvas (typically 3) and sends them all in one request. Server
// runs the vision model on each frame and merges the matches by
// scientific name.
export interface IdentifyVideoRequest {
  frames: string[]; // 1..5 data-URI frames
  location_name: string;
  candidates: IdentifyCandidate[];
}
export interface IdentifyVideoResponse extends IdentifyResponse {
  frames_analysed: number;
  frames_failed?: number;
}

// ----- /api/lookalikes -----

export interface LookalikeRequest {
  target: { common: string; scientific: string };
  location_name: string;
  candidates: IdentifyCandidate[];
}

export interface LookalikeItem {
  scientific_name: string;
  common_name: string;
  distinction: string; // very short: how to tell it apart from the target
}

export interface LookalikeResponse {
  lookalikes: LookalikeItem[];
}

// ----- /api/recategorize (one-time backfill of missing categories) -----

export interface RecategorizeRequest {
  species: { taxon_id: number; scientific_name: string }[];
}

export interface RecategorizeResponse {
  categories: Record<string, string>; // taxon_id -> category
  // Number of species this call couldn't resolve because it hit the
  // per-request GBIF fanout cap. Non-zero means the client should
  // requeue the leftover ids in a subsequent call.
  remaining?: number;
}

// ----- /api/placenames (one-time backfill of legacy "My location" /
// bare-coordinate visited spots into real place names) -----

export interface PlaceNamesRequest {
  points: { key: string; lat: number; lng: number }[];
}

export interface PlaceNamesResponse {
  names: Record<string, string>; // location key -> resolved place name
}

// ----- /api/geosearch (place autocomplete for the location dropdown) -----

export interface PlaceResult {
  name: string; // concise place name, e.g. "Hanauma Bay Nature Preserve"
  detail: string; // region/context, e.g. "Honolulu, Hawaii" (may be "")
  lat: number;
  lng: number;
}

export interface GeoSearchResponse {
  results: PlaceResult[];
}

// ----- /api/factsheet (species detail) -----

export interface FactsheetRequest {
  taxon_id: number;
  common: string;
  scientific: string;
}

// Per-source outcome so the UI can say *what* degraded (and the API caller can
// see why a card is thin). "error" = the source call/parse failed (iNat 429,
// AI transport/JSON/token-budget); "empty" = it ran but had nothing; "skipped"
// = not attempted (e.g. iNat for a synthetic GBIF/OBIS-only taxon id).
export type SourceStatus = "ok" | "empty" | "skipped" | "error";

export interface FactsheetSources {
  inat: SourceStatus; // Wikipedia summary + IUCN status
  gbif: SourceStatus; // distribution-map taxon key
  ai: SourceStatus; // diet/size/habitat/behavior
  description: "inat" | "wikipedia" | "ai" | "none"; // where the blurb came from
}

export interface Factsheet {
  description: string; // iNat/Wikipedia/AI summary, plain text ("" if none)
  source_url: string; // Wikipedia URL ("" if none / AI-written)
  conservation: string; // e.g. "Least Concern (IUCN)" ("" if unknown)
  endangered: boolean; // heuristic from conservation status
  diet: string;
  size: string;
  habitat: string;
  behavior: string;
  where_found: string;
  ai_generated: boolean; // diet/size/habitat/behavior came from the model
  gbif_taxon_key: number | null; // for the distribution map tiles
  cached: boolean; // served from the ~30d cache (no fresh iNat/GBIF/AI calls)
  sources: FactsheetSources; // per-source diagnostics
}

export interface ApiError {
  error: string;
}

// ----- Persisted state (localStorage + durable KV) -----

export interface LifelistEntry {
  taxon_id: number;
  common_name: string;
  scientific_name: string;
  observation_count: number;
  first_seen: string; // ISO timestamp (first logged)
  last_seen?: string; // ISO — bumped on every (re)tap; falls back to first_seen
  location_name: string;
  photo_url: string;
  trip_ids: string[]; // every trip this fish has been logged on
  category?: string; // taxonomic group (for the by-type breakdown)
}

export interface VisitedLocation {
  key: string; // rounded lat,lng — stable across name variations
  name: string;
  lat: number;
  lng: number;
  radius_km: number;
  research_only: boolean;
  last_visited: string; // ISO
  cached_species: Species[];
  cached_at: string; // ISO — 24h TTL
  // sort_version that produced cached_species. When the server bumps its
  // sort_version (see /api/species response), any cached_species stamped
  // with a different value is discarded on read — so a sort-order rev
  // takes effect without a manual VISIT_CACHE_VERSION bump.
  sort_version?: string;
}

export interface Badge {
  location_key: string;
  location_name: string;
  threshold: number; // 25 | 50 | 75 | 100
  unlocked_at: string; // ISO
}

export interface Trip {
  id: string;
  name: string;
  created_at: string; // ISO
  /**
   * Named snorkel spots the trip visited — e.g. ["Two Step", "Manta Point"].
   * Free-text strings matched against a curated shared list on entry, with
   * a "add anyway" fallback for anything not in the list. Attached to the
   * trip rather than to individual fish so the user isn't asked to name a
   * beach every time they log a sighting; instead a beach is a durable
   * marker of "we went here on this trip." Order is insertion (most recent
   * first). Case-insensitively de-duplicated on write.
   */
  beaches?: string[];
}

export interface FishFinderState {
  lifelist: LifelistEntry[];
  visited_locations: VisitedLocation[];
  badges: Badge[];
  trips: Trip[];
  active_trip_id?: string | null; // new sightings auto-join this trip
}

// ----- Feature/bug tracker -----

export type FeedbackKind = "bug" | "feature";
export type FeedbackStatus = "open" | "planned" | "in_progress" | "closed";

export interface FeedbackItem {
  id: string;
  kind: FeedbackKind;
  title: string;
  body: string;
  status: FeedbackStatus;
  created_at: string; // ISO
  updated_at: string; // ISO
  // Only populated for admins viewing the full queue.
  submitter_email?: string;
  // True when the signed-in user submitted this item.
  mine: boolean;
}

export interface AuthUser {
  email: string;
  is_admin: boolean;
  username?: string | null; // @handle, unique when set
  display_name?: string | null; // freeform, up to 60 chars
  email_verified?: boolean;
}

// ----- Billing -----

// Free-tier caps. Imported by both the client (UI) and the Worker
// (server-side enforcement) so they can't drift.
export const FREE_TRIP_LIMIT = 1;
export const FREE_LOCATION_LIMIT = 3;
// Length of the all-access trial every new account starts with.
export const TRIAL_DAYS = 7;

export type BillingPlan = "free" | "pro";
// "subscription" = recurring monthly/yearly; "onetime" = lifetime unlock.
export type PurchaseType = "subscription" | "onetime";

export interface BillingState {
  plan: BillingPlan;
  // Stripe-style status: active | trialing | past_due | canceled | incomplete | …
  status: string;
  // null for free / lifetime; ISO timestamp for an active subscription's
  // current period end (used to render "renews on" / "access until").
  current_period_end: string | null;
  // True when an active subscription is set to cancel at period end.
  cancel_at_period_end: boolean;
  // Which path the user took. null when on free or for legacy stub upgrades.
  purchase_type: PurchaseType | null;
  // "stub" => no Stripe keys; "stripe" => live integration.
  provider: "stub" | "stripe";
  // True when at least a monthly subscription price is configured.
  subscription_available: boolean;
  // True when a one-time price is configured.
  onetime_available: boolean;
  // ISO timestamp the all-access trial ends (null once it's irrelevant).
  trial_ends_at: string | null;
  // True while the trial is currently granting all-access (plan is "free"
  // but the user has Pro-level entitlement until trial_ends_at).
  in_trial: boolean;
}

// ----- Buddies (social sharing) -----

// Free-tier cap on accepted buddies. Pending invites don't count.
export const FREE_BUDDY_LIMIT = 3;

// Public-facing shape for another user. Everything is optional so a user
// who's set no display name / username still renders as their email prefix.
export interface BuddyProfile {
  user_id: string;
  email?: string; // omitted in some contexts (e.g. public overlap listings)
  username: string | null; // @handle, unique lowercase [a-z0-9_]
  display_name: string | null; // freeform, up to 60 chars
}

// A confirmed two-way buddy relationship.
export interface Buddy {
  profile: BuddyProfile;
  since: string; // ISO — when both sides accepted
}

// Directional pending invite record shown in the UI.
export interface BuddyInvite {
  id: string;
  // The other party as we know them at invite time.
  from: BuddyProfile | null;
  to_email: string | null; // populated for email-based invites
  to_username: string | null; // populated for @username invites
  status: "pending" | "accepted" | "rejected" | "expired";
  created_at: string; // ISO
  expires_at: string; // ISO
}

export interface BuddyListResponse {
  buddies: Buddy[];
  invites_received: BuddyInvite[];
  invites_sent: BuddyInvite[];
  cap: number; // free-tier cap that applies right now (Infinity → -1 wire form)
  slots_remaining: number; // -1 when unlimited
}

export interface BuddyInviteRequest {
  // Exactly one of these two must be non-empty on the wire.
  email?: string;
  username?: string;
}

export interface BuddyInviteResponse {
  // Populated on the normal path (new invite recorded / existing refreshed).
  invite?: BuddyInvite;
  // True when we successfully sent an email invite; false when Resend isn't
  // configured or the recipient is already a Reefscope user (in-app only).
  emailed?: boolean;
  // True when both sides had already invited each other; no invite row is
  // returned because a buddy pair was formed immediately.
  auto_accepted?: boolean;
  // Populated on the auto-accept path so the client can render "you and X
  // are now buddies" without a follow-up round-trip.
  buddy?: Buddy;
}

export interface ProfileUpdateRequest {
  username?: string | null; // null clears it
  display_name?: string | null;
}

// Buddy overlap on the species detail page. One entry per buddy who has
// logged this scientific_name in their lifelist.
export interface BuddySighting {
  profile: BuddyProfile; // avatar + name identity
  taxon_id: number; // theirs — may differ from the current user's
  common_name: string; // per their lifelist row (in case of a rename)
  scientific_name: string;
  location_name: string;
  photo_url: string;
  last_seen: string; // ISO
}

export interface BuddySpeciesResponse {
  sightings: BuddySighting[]; // sorted by last_seen desc
}

// Full lifelist of a single buddy, so I can browse their collection
// and filter to species they've seen that I haven't. trip_ids is
// stripped by the server — buddies see the species, not the private
// trip structure.
export interface BuddyLifelistResponse {
  profile: BuddyProfile;
  entries: LifelistEntry[]; // sorted by last_seen desc
}

// ----- Trip URLs (Feature 1) -----

export interface TripShare {
  slug: string;
  trip_id: string;
  mode: "public" | "buddies";
  created_at: string; // ISO
  expires_at: string; // ISO
  revoked: boolean;
}

export interface TripShareResponse {
  share: TripShare | null;
}

// Payload the public /t/:slug page renders.
export interface SharedTripSpecies {
  taxon_id: number;
  common_name: string;
  scientific_name: string;
  photo_url: string;
  first_seen: string;
  last_seen: string;
  location_name: string;
  category?: string;
  // Public iNaturalist sighting count — powers the rarity badge on the
  // shared page. Not private (it's a global stat about the species).
  observation_count?: number;
}

// ----- Shared trips (Feature 3) -----

// One accepted participant on a shared trip.
export interface TripMember {
  profile: BuddyProfile;
  role: "owner" | "member";
  since: string; // ISO — empty string for the synthetic owner entry
}

// Someone the owner or a member invited who hasn't accepted yet.
export interface TripMemberInvite {
  member_user_id: string;
  profile: BuddyProfile;
  invited_by: string;
  invited_at: string;
}

export interface TripMembersResponse {
  owner: TripMember | null;
  members: TripMember[];
  invites: TripMemberInvite[];
  caller_is_owner: boolean;
  caller_is_member: boolean;
}

// A species logged to a shared trip, attributed to the logger.
export interface TripSharedSighting {
  id: string;
  taxon_id: number;
  scientific_name: string;
  common_name: string;
  photo_url: string;
  location_name: string;
  logged_at: string;
  logged_by: BuddyProfile | null;
}

export interface TripSharedSightingsResponse {
  sightings: TripSharedSighting[];
}

// "Alice logged parrotfish — add to your lifelist?" per-target prompt.
export interface TripLogInvite {
  id: string;
  trip_id: string;
  trip_name: string;
  from: BuddyProfile | null;
  taxon_id: number;
  scientific_name: string;
  common_name: string;
  photo_url: string;
  location_name: string;
  created_at: string;
}

export interface TripLogInvitesResponse {
  invites: TripLogInvite[];
}

// "Emma invited you to join her trip 'Manta Point'" — pending trip
// membership across every trip the caller has been invited to.
export interface PendingTripMembership {
  trip_id: string;
  trip_name: string;
  owner: BuddyProfile | null;
  invited_by: BuddyProfile | null;
  invited_at: string;
}

export interface PendingTripMembershipsResponse {
  invites: PendingTripMembership[];
}

export interface SharedTripResponse {
  trip: {
    id: string;
    name: string;
    created_at: string;
  };
  species: SharedTripSpecies[];
  owner: {
    user_id: string;
    username: string | null;
    display_name: string | null;
  };
  // First matching location — used to plot a pin. Omitted if the owner has
  // no visited-location coordinates covering the trip roster.
  location: {
    lat: number;
    lng: number;
    name: string | null;
  } | null;
  // Every one of the trip's own beaches that has coordinates — for a
  // multi-pin map. Only the trip's declared beaches (never arbitrary
  // lifelist location names) so a home reef can't leak.
  spots: { name: string; lat: number; lng: number }[];
  // Number of accepted buddies who joined this trip (excludes the owner).
  // 0 for a solo trip.
  buddy_count: number;
  mode: "public" | "buddies";
}
